export interface UniformMeta {
  type: 'float' | 'color' | 'vec2' | 'vec3';
  default: number | string | number[];
  min?: number;
  max?: number;
  step?: number;
  label: string;
}

export interface PbrShaderChunks {
  /** Spliced before '#include <common>' in the vertex shader (varying decls) */
  vertexDecl: string;
  /** Spliced after '#include <begin_vertex>' in the vertex shader */
  vertexMain: string;
  /** Spliced before '#include <common>' in the fragment shader (uniforms, helpers) */
  fragmentDecl: string;
  /** Spliced after '#include <emissivemap_fragment>' in the fragment shader.
   *  At that point `diffuseColor` (rgba, pre-lighting), `totalEmissiveRadiance`,
   *  `normal` (view space) and `vViewPosition` are all live — mutate diffuse for
   *  surface patterns, add to emissive for glows/sparkles, mutate
   *  diffuseColor.a for transparency. */
  fragmentMain: string;
}

export interface ShaderPresetDef {
  label: string;
  description: string;
  uniforms: Record<string, UniformMeta>;
  vertexShader: string;
  fragmentShader: string;
  /** All current presets are PBR: a patched MeshPhysicalMaterial (real
   *  lighting + env map) with pbrChunks spliced in via onBeforeCompile.
   *  vertexShader / fragmentShader are unused ('') on PBR presets — they
   *  remain for potential raw-ShaderMaterial presets. The uniform keys
   *  tint/baseColor/roughness/metalness/clearcoat map onto material
   *  properties, not shader uniforms (see buildPbrShader). */
  pbr?: boolean;
  pbrChunks?: PbrShaderChunks;
  /** PBR material flags applied at build time */
  pbrOptions?: { transparent?: boolean; doubleSided?: boolean };
  /** The preset consumes config.map as its pattern texture, exposed to the
   *  fragment chunks as `sampler2D patternMap` + `float hasPattern` (0/1).
   *  The editor renders an icon drop zone for it. */
  usesTexture?: boolean;
}

/** PBR sliders shared by every preset — spread last so they always appear
 *  at the bottom of the uniforms panel in the same order. */
const pbrUniforms = (roughness: number, metalness: number, clearcoat: number): Record<string, UniformMeta> => ({
  roughness: { type: 'float', default: roughness, min: 0.0, max: 1.0, step: 0.01, label: 'Roughness' },
  metalness: { type: 'float', default: metalness, min: 0.0, max: 1.0, step: 0.01, label: 'Metalness' },
  clearcoat: { type: 'float', default: clearcoat, min: 0.0, max: 1.0, step: 0.01, label: 'Clearcoat' },
});

export const SHADER_PRESETS: Record<string, ShaderPresetDef> = {
  holographic: {
    label: 'Holographic',
    description: 'Iridescent color shifting based on view angle',
    uniforms: {
      speed: { type: 'float', default: 1.0, min: 0.1, max: 5.0, step: 0.1, label: 'Speed' },
      intensity: { type: 'float', default: 1.0, min: 0.0, max: 2.0, step: 0.05, label: 'Intensity' },
      tint: { type: 'color', default: '#ffffff', label: 'Base Color' },
      ...pbrUniforms(0.35, 0.4, 0.5),
    },
    pbr: true,
    pbrChunks: {
      vertexDecl: '',
      vertexMain: '',
      fragmentDecl: /* glsl */ `
uniform float time;
uniform float speed;
uniform float intensity;
`,
      fragmentMain: /* glsl */ `
{
  vec3 sdlNrm = normalize(normal);
  float sdlFres = pow(1.0 - saturate(dot(sdlNrm, normalize(vViewPosition))), 2.0);
  float sdlShift = time * speed + dot(sdlNrm, vec3(1.0, 0.5, 0.25)) * 3.0;
  vec3 sdlRainbow = vec3(
    sin(sdlShift) * 0.5 + 0.5,
    sin(sdlShift + 2.094) * 0.5 + 0.5,
    sin(sdlShift + 4.189) * 0.5 + 0.5);
  totalEmissiveRadiance += sdlRainbow * sdlFres * intensity;
}
`,
    },
    vertexShader: '',
    fragmentShader: '',
  },

  metallicFlake: {
    label: 'Metallic Flake',
    description: 'Sparkling metallic particles',
    uniforms: {
      flakeScale: { type: 'float', default: 50.0, min: 10.0, max: 200.0, step: 1.0, label: 'Flake Scale' },
      sparkle: { type: 'float', default: 0.8, min: 0.0, max: 1.0, step: 0.05, label: 'Sparkle' },
      baseColor: { type: 'color', default: '#222222', label: 'Base Color' },
      flakeColor: { type: 'color', default: '#ffffff', label: 'Flake Color' },
      ...pbrUniforms(0.4, 0.6, 0.4),
    },
    pbr: true,
    pbrChunks: {
      // Flakes anchored in OBJECT space so they tumble with the die
      vertexDecl: /* glsl */ `
varying vec3 vSdlLocalPos;
`,
      vertexMain: /* glsl */ `
vSdlLocalPos = position;
`,
      fragmentDecl: /* glsl */ `
uniform float time;
uniform float flakeScale;
uniform float sparkle;
uniform vec3 flakeColor;
varying vec3 vSdlLocalPos;

float sdlHash(vec3 p) {
  p = fract(p * vec3(443.897, 441.423, 437.195));
  p += dot(p, p.yzx + 19.19);
  return fract((p.x + p.y) * p.z);
}
`,
      fragmentMain: /* glsl */ `
{
  float sdlFlake = sdlHash(floor(vSdlLocalPos * flakeScale));
  float sdlIntensity = smoothstep(0.7, 1.0, sdlFlake) * sparkle;
  sdlIntensity *= sin(time * 3.0 + sdlFlake * 6.28) * 0.5 + 0.5;
  totalEmissiveRadiance += flakeColor * sdlIntensity;
}
`,
    },
    vertexShader: '',
    fragmentShader: '',
  },

  glitter: {
    label: 'Glitter',
    description: 'Dense glitter in a tinted base with real PBR lighting',
    uniforms: {
      tint: { type: 'color', default: '#ff3fa4', label: 'Base Color' },
      glitterColor: { type: 'color', default: '#ffc4e5', label: 'Glitter Color' },
      glitterColor2: { type: 'color', default: '#ffffff', label: 'Glitter Accent' },
      density: { type: 'float', default: 220.0, min: 40.0, max: 600.0, step: 5.0, label: 'Density' },
      coverage: { type: 'float', default: 0.6, min: 0.0, max: 1.0, step: 0.05, label: 'Coverage' },
      brightness: { type: 'float', default: 2.5, min: 0.0, max: 6.0, step: 0.1, label: 'Brightness' },
      depth: { type: 'float', default: 0.12, min: 0.0, max: 0.4, step: 0.01, label: 'Suspension Depth' },
      twinkleSpeed: { type: 'float', default: 1.5, min: 0.0, max: 8.0, step: 0.1, label: 'Twinkle Speed' },
      ...pbrUniforms(0.35, 0.15, 0.6),
    },
    pbr: true,
    // The base shades through the standard lighting + env map path (opaque,
    // roughness/metalness/clearcoat all real). Only the sparkle layer is
    // custom, added as emissive so the glints punch through regardless of
    // light direction.
    pbrChunks: {
      vertexDecl: /* glsl */ `
varying vec3 vSdlLocalPos;
varying vec3 vSdlLocalViewDir;
`,
      // Glitter cells live in OBJECT space so they tumble with the die
      // (world-space cells swim when the die moves). The object-space view dir
      // (parallax + glints) is the world view dir rotated back through the
      // model rotation — manual transpose, rigid transform, GLSL1-safe.
      vertexMain: /* glsl */ `
vSdlLocalPos = position;
vec4 sdlWorldPos = modelMatrix * vec4(position, 1.0);
vec3 sdlWorldViewDir = cameraPosition - sdlWorldPos.xyz;
mat3 sdlModelRot = mat3(modelMatrix);
vSdlLocalViewDir = normalize(vec3(dot(sdlModelRot[0], sdlWorldViewDir), dot(sdlModelRot[1], sdlWorldViewDir), dot(sdlModelRot[2], sdlWorldViewDir)));
`,
      fragmentDecl: /* glsl */ `
uniform float time;
uniform vec3 glitterColor;
uniform vec3 glitterColor2;
uniform float density;
uniform float coverage;
uniform float brightness;
uniform float depth;
uniform float twinkleSpeed;
varying vec3 vSdlLocalPos;
varying vec3 vSdlLocalViewDir;

float sdlHash(vec3 p) {
  p = fract(p * vec3(443.897, 441.423, 437.195));
  p += dot(p, p.yzx + 19.19);
  return fract((p.x + p.y) * p.z);
}

vec3 sdlHash3(vec3 p) {
  return vec3(sdlHash(p), sdlHash(p + 17.17), sdlHash(p + 31.31));
}
`,
      // Three glitter layers marched into the volume along the view ray for a
      // suspended-in-resin depth read. Each flake has a random facing; only
      // flakes aligned with the eye catch light, so rotating the die makes
      // different flakes pop.
      fragmentMain: /* glsl */ `
{
  float sparkleSum = 0.0;
  vec3 sparkleCol = vec3(0.0);
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float layerFade = 1.0 - fi * 0.3;
    vec3 p = (vSdlLocalPos - vSdlLocalViewDir * depth * fi) * density * (1.0 + fi * 0.37);
    vec3 rnd = sdlHash3(floor(p));
    float present = smoothstep(1.0 - coverage, 1.0, rnd.x);
    vec3 flakeNormal = normalize(rnd * 2.0 - 1.0);
    float glint = pow(abs(dot(flakeNormal, vSdlLocalViewDir)), 24.0);
    // Sharp sparse pops, not a sine throb: each flake holds a steady base
    // glint, then briefly flashes ~3x on its own phase AND rate, so at any
    // moment only a scattered few are popping
    float ph = fract(rnd.y + time * twinkleSpeed * (0.09 + 0.12 * rnd.z));
    float flash = pow(max(sin(ph * 6.2831), 0.0), 20.0);
    float twinkle = 0.4 + 2.4 * flash;
    float s = present * glint * twinkle * layerFade;
    sparkleSum += s;
    sparkleCol += mix(glitterColor, glitterColor2, rnd.z) * s;
  }
  totalEmissiveRadiance += sparkleCol * brightness;
  totalEmissiveRadiance += vec3(1.0) * sparkleSum * sparkleSum * brightness * 0.15;
}
`,
    },
    vertexShader: '',
    fragmentShader: '',
  },

  pattern: {
    label: 'Pattern',
    description: 'An icon tiled over a base color. Drop an image (transparent PNG works best) to repeat it across the die',
    usesTexture: true,
    uniforms: {
      tint: { type: 'color', default: '#23232a', label: 'Base Color' },
      iconColor: { type: 'color', default: '#ffffff', label: 'Icon Tint' },
      patternScale: { type: 'float', default: 4.0, min: 1.0, max: 40.0, step: 0.5, label: 'Density' },
      iconScale: { type: 'float', default: 0.75, min: 0.1, max: 1.0, step: 0.01, label: 'Icon Scale' },
      stagger: { type: 'float', default: 0.0, min: 0.0, max: 1.0, step: 0.05, label: 'Row Stagger' },
      rotation: { type: 'float', default: 0.0, min: 0.0, max: 360.0, step: 1.0, label: 'Rotation' },
      rotationJitter: { type: 'float', default: 0.0, min: 0.0, max: 180.0, step: 1.0, label: 'Rotation Jitter' },
      iconEmissive: { type: 'float', default: 0.0, min: 0.0, max: 3.0, step: 0.05, label: 'Icon Glow' },
      ...pbrUniforms(0.45, 0.1, 0.35),
    },
    pbr: true,
    pbrChunks: {
      vertexDecl: /* glsl */ `
varying vec2 vSdlUv;
`,
      vertexMain: /* glsl */ `
vSdlUv = uv;
`,
      fragmentDecl: /* glsl */ `
uniform sampler2D patternMap;
uniform float hasPattern;
uniform vec3 iconColor;
uniform float patternScale;
uniform float iconScale;
uniform float stagger;
uniform float rotation;
uniform float rotationJitter;
uniform float iconEmissive;
varying vec2 vSdlUv;

float sdlHash2(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
`,
      // Each tile cell holds the icon centered at iconScale of the cell —
      // 1.0 packs icons edge to edge (a full grid), smaller values leave base
      // color showing between them. Stagger shifts alternate rows for a
      // brick / wallpaper layout. Rotation spins every icon; jitter adds a
      // per-icon random angle on top. Icon glow feeds the icons (only) into
      // the emissive term.
      fragmentMain: /* glsl */ `
{
  vec2 sdlGrid = vSdlUv * patternScale;
  if (mod(floor(sdlGrid.y), 2.0) > 0.5) sdlGrid.x += stagger;
  vec2 sdlCellId = floor(sdlGrid);
  vec2 sdlCell = fract(sdlGrid);
  float sdlAng = radians(rotation) + (sdlHash2(sdlCellId) - 0.5) * 2.0 * radians(rotationJitter);
  float sdlCos = cos(sdlAng);
  float sdlSin = sin(sdlAng);
  vec2 sdlP = sdlCell - 0.5;
  sdlP = vec2(sdlCos * sdlP.x + sdlSin * sdlP.y, -sdlSin * sdlP.x + sdlCos * sdlP.y);
  float sdlSpan = clamp(iconScale, 0.05, 1.0);
  vec2 sdlIconUv = sdlP / sdlSpan + 0.5;
  if (hasPattern > 0.5 &&
      sdlIconUv.x >= 0.0 && sdlIconUv.x <= 1.0 &&
      sdlIconUv.y >= 0.0 && sdlIconUv.y <= 1.0) {
    vec4 sdlIcon = texture2D(patternMap, sdlIconUv);
    diffuseColor.rgb = mix(diffuseColor.rgb, sdlIcon.rgb * iconColor, sdlIcon.a);
    totalEmissiveRadiance += sdlIcon.rgb * iconColor * sdlIcon.a * iconEmissive;
  }
}
`,
    },
    vertexShader: '',
    fragmentShader: '',
  },

  neonGlow: {
    label: 'Neon Glow',
    description: 'Fresnel-based emissive edge glow',
    uniforms: {
      glowColor: { type: 'color', default: '#00f5ff', label: 'Glow Color' },
      glowIntensity: { type: 'float', default: 2.0, min: 0.0, max: 10.0, step: 0.1, label: 'Intensity' },
      fresnelPower: { type: 'float', default: 2.0, min: 0.5, max: 5.0, step: 0.1, label: 'Edge Sharpness' },
      baseColor: { type: 'color', default: '#111111', label: 'Base Color' },
      pulseSpeed: { type: 'float', default: 1.0, min: 0.0, max: 5.0, step: 0.1, label: 'Pulse Speed' },
      ...pbrUniforms(0.5, 0.1, 0.3),
    },
    pbr: true,
    pbrChunks: {
      vertexDecl: '',
      vertexMain: '',
      fragmentDecl: /* glsl */ `
uniform float time;
uniform vec3 glowColor;
uniform float glowIntensity;
uniform float fresnelPower;
uniform float pulseSpeed;
`,
      fragmentMain: /* glsl */ `
{
  float sdlFres = pow(1.0 - saturate(dot(normalize(normal), normalize(vViewPosition))), fresnelPower);
  float sdlPulse = pulseSpeed > 0.0 ? sin(time * pulseSpeed) * 0.3 + 0.7 : 1.0;
  totalEmissiveRadiance += glowColor * sdlFres * glowIntensity * sdlPulse;
}
`,
    },
    vertexShader: '',
    fragmentShader: '',
  },

  glass: {
    label: 'Glass',
    description: 'Transparent with Fresnel reflection',
    uniforms: {
      tint: { type: 'color', default: '#aaddff', label: 'Tint' },
      baseOpacity: { type: 'float', default: 0.15, min: 0.0, max: 1.0, step: 0.01, label: 'Base Opacity' },
      fresnelPower: { type: 'float', default: 3.0, min: 0.5, max: 8.0, step: 0.1, label: 'Fresnel Power' },
      reflectivity: { type: 'float', default: 0.5, min: 0.0, max: 1.0, step: 0.05, label: 'Reflectivity' },
      ...pbrUniforms(0.05, 0.0, 0.9),
    },
    pbr: true,
    pbrOptions: { transparent: true, doubleSided: true },
    pbrChunks: {
      vertexDecl: '',
      vertexMain: '',
      fragmentDecl: /* glsl */ `
uniform float baseOpacity;
uniform float fresnelPower;
uniform float reflectivity;
`,
      // Edges brighten (emissive fresnel) and go opaque; the face stays see-
      // through at baseOpacity. Real env reflections come from the PBR path.
      fragmentMain: /* glsl */ `
{
  float sdlFres = pow(1.0 - saturate(dot(normalize(normal), normalize(vViewPosition))), fresnelPower);
  totalEmissiveRadiance += vec3(sdlFres * reflectivity * 0.6);
  diffuseColor.a = baseOpacity + sdlFres * (1.0 - baseOpacity);
}
`,
    },
    vertexShader: '',
    fragmentShader: '',
  },

  carbonFiber: {
    label: 'Carbon Fiber',
    description: 'Procedural woven carbon fiber pattern',
    uniforms: {
      scale: { type: 'float', default: 30.0, min: 5.0, max: 100.0, step: 1.0, label: 'Pattern Scale' },
      baseColor: { type: 'color', default: '#1a1a1a', label: 'Base Color' },
      highlightColor: { type: 'color', default: '#444444', label: 'Highlight' },
      glossiness: { type: 'float', default: 0.8, min: 0.0, max: 1.0, step: 0.05, label: 'Glossiness' },
      ...pbrUniforms(0.35, 0.5, 0.6),
    },
    pbr: true,
    pbrChunks: {
      vertexDecl: /* glsl */ `
varying vec2 vSdlUv;
`,
      vertexMain: /* glsl */ `
vSdlUv = uv;
`,
      fragmentDecl: /* glsl */ `
uniform float scale;
uniform vec3 highlightColor;
uniform float glossiness;
varying vec2 vSdlUv;
`,
      fragmentMain: /* glsl */ `
{
  vec2 sdlUvS = vSdlUv * scale;
  float sdlWeaveX = sin(sdlUvS.x * 3.14159) * 0.5 + 0.5;
  float sdlWeaveY = sin(sdlUvS.y * 3.14159) * 0.5 + 0.5;
  float sdlSel = step(0.5, mod(floor(sdlUvS.x) + floor(sdlUvS.y), 2.0));
  float sdlWeave = mix(sdlWeaveX, sdlWeaveY, sdlSel);
  diffuseColor.rgb = mix(diffuseColor.rgb, highlightColor, sdlWeave * 0.5);
  float sdlFres = pow(1.0 - saturate(dot(normalize(normal), normalize(vViewPosition))), 3.0);
  totalEmissiveRadiance += vec3(1.0) * sdlFres * glossiness * 0.15;
}
`,
    },
    vertexShader: '',
    fragmentShader: '',
  },
};
